import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FileDataService } from 'src/app/core/services/api-services';
import { FileDataModel } from 'src/app/core/services/api-services';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { AppConfigService } from 'src/app/core/services/app-config.service';
import { MimeTypes } from 'src/app/core/constants/mime-types';

@Component({
    selector: 'app-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.scss'],
    standalone: false
})
export class UploadComponent implements OnInit {

  files: NgxFileDropEntry[] = [];
  fileErrorTranslationCode?: string;

  transValue: any;

  documentName?: string;

  @Input() documents: FileDataModel[] = [];
  @Input() title?: string;
  @Input() allowedFileExtensions?: string[] = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'jpg', 'jpeg', 'png'];
  @Input() allowedMimeTypes?: string[];
  @Input() maxFileSize?: number;
  @Input() multiple?: boolean;
  @Input() disabled?: boolean;
  @Input() singleFileOnly?: boolean;
  @Input() openInsteadOfDownload?: boolean;
  @Input() showBottom: boolean = false;
  @Input() hiddenDocuments: FileDataModel[] = [];
  @Input() canEdit: boolean = true;

  @Output() dropped: EventEmitter<FileDataModel> = new EventEmitter<FileDataModel>();
  @Output() removedDocument: EventEmitter<FileDataModel> = new EventEmitter<FileDataModel>();

  constructor(
    private readonly fileDataService: FileDataService,
    private readonly appConfigService: AppConfigService
  ) { }

  ngOnInit(): void {
  }

  onDrop(files: NgxFileDropEntry[]): void {
    this.fileErrorTranslationCode = undefined;
    this.documentName = undefined;
    this.files = files;
    for (const droppedFile of files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const splitedName = droppedFile.fileEntry.name.split('.');
        const extension = splitedName.pop()?.toLowerCase();
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {

          if (this.maxFileSize && (file.size / 1024) > this.maxFileSize) {
            this.fileErrorTranslationCode = 'Common.Upload.FileSizeTooLarge';
            this.transValue = { fileSize: this.maxFileSize / 1024 };

            return;
          }
          
          if (this.allowedFileExtensions && !this.allowedFileExtensions.filter(x => x === extension)?.length) {
            this.fileErrorTranslationCode = 'Common.Upload.ExtensionNotAllowed';

            return;
          }

          if (this.documents.filter(x => x.filename === file.name)?.length > 0 || this.hiddenDocuments.filter(x => x.filename === file.name)?.length > 0) {
            this.fileErrorTranslationCode = 'Common.Upload.SameDocumentName';
            this.transValue = { documentName: file.name };

            return;
          }

          if (droppedFile.fileEntry.isFile) {
            const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
            fileEntry.file((file: File) => {

              // The MIME type string itself (not a boolean), used for the allowed-types check and as contentType.
              const mimeType: string = file.type || this.getMimeTypeByExtension(extension);

              if (this.allowedMimeTypes && !this.allowedMimeTypes.filter(x => x === mimeType)?.length) {
                this.fileErrorTranslationCode = 'Common.Upload.ExtensionNotAllowed';
                return;
              }

              const byteReader = new FileReader();

              byteReader.onload = () => {
                const dataUrl = byteReader.result as string;

                const fileData = {
                  data: dataUrl.split(',')[1],
                  filename: file.name,
                  contentType: mimeType
                } as FileDataModel;

                this.dropped.emit(fileData);
              };

              byteReader.readAsDataURL(file);
            });
          }
        });
      }
    }
  }

  removeDocument(document: FileDataModel): void {
    const index: number = this.documents.indexOf(document);
    if (index !== -1) {
      this.removedDocument.emit({ ...document });
    }
  }

  private getMimeTypeByExtension(extension?: string): string {
    switch (extension) {
      case 'pdf':
        return MimeTypes.pdf;
      case 'jpg':
      case 'jpeg':
        return MimeTypes.jpeg;
      case 'png':
        return MimeTypes.png;
      default:
        return '';
    }
  }

  downloadDocument(document: FileDataModel): void {
    if (document.id) {
      this.fileDataService.get(document.id!).subscribe(data => {
        this.downloadFile(data!);
      });
    } else {
      this.downloadFile(document);
    }
  }

  private downloadFile(data: FileDataModel): void {
    const binaryString = window.atob(data.data!);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: data.contentType });
    const url = window.URL.createObjectURL(blob);
    
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.setAttribute('download', data.filename);
    
    if (this.openInsteadOfDownload) {
      window.open(`${this.appConfigService.getConfig().apiUrl}/api/filedata/${data.id}`, '_blank');
    } else {
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(url);
    }
  }
}
